import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Building2,
  ChevronRight,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search as SearchIcon,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBanner } from "@/components/ui/status-banner";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import type { Company } from "@/lib/types";

export const Route = createFileRoute("/clients")({
  component: ClientsPage,
});

function ClientsPage() {
  return (
    <LoginGate title="DeepDive" subtitle="Iniciá sesión para ver clientes.">
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <ClientsContent />
        </AppShell>
      )}
    </LoginGate>
  );
}

function ClientsContent() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    title: string;
  } | null>(null);

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ["companies", "list"],
    queryFn: async (): Promise<Company[]> => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Company[];
    },
  });

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return companies.filter((c) => {
      if (!showInactive && !c.is_active) return false;
      if (!t) return true;
      return (
        c.name.toLowerCase().includes(t) ||
        (c.razon_social ?? "").toLowerCase().includes(t) ||
        (c.email ?? "").toLowerCase().includes(t) ||
        (c.rubro ?? "").toLowerCase().includes(t)
      );
    });
  }, [companies, search, showInactive]);

  const activeCount = companies.filter((c) => c.is_active).length;
  const inactiveCount = companies.length - activeCount;

  const toggleActiveMutation = useMutation({
    mutationFn: async ({
      id,
      is_active,
    }: {
      id: string;
      is_active: boolean;
    }) => {
      const { error } = await supabase
        .from("companies")
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { is_active }) => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      setFeedback({
        kind: "success",
        title: is_active ? "Cliente activado" : "Cliente desactivado",
      });
    },
    onError: (e: Error) => setFeedback({ kind: "error", title: e.message }),
  });

  return (
    <div className="px-6 py-5">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Clientes</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {activeCount} {activeCount === 1 ? "empresa activa" : "empresas activas"}
            {inactiveCount > 0 && ` · ${inactiveCount} inactiva${inactiveCount === 1 ? "" : "s"}`}
          </p>
        </div>
        <Button variant="primary" asChild>
          <Link to="/clients/new">
            <Plus className="h-4 w-4" />
            Nuevo cliente
          </Link>
        </Button>
      </div>

      {feedback && (
        <div className="mb-6">
          <StatusBanner
            variant={feedback.kind}
            title={feedback.title}
            onDismiss={() => setFeedback(null)}
          />
        </div>
      )}

      <div className="rounded-[var(--radius)] border border-border bg-card p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar por nombre, razón social, email, rubro"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
          <Button
            variant={showInactive ? "primary" : "default"}
            size="sm"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? "Ocultar inactivos" : "Ver inactivos"}
          </Button>
        </div>
      </div>

      <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState empty={companies.length === 0} />
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((c) => (
              <CompanyRow
                key={c.id}
                company={c}
                onToggleActive={(is_active) =>
                  toggleActiveMutation.mutate({ id: c.id, is_active })
                }
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EmptyState({ empty }: { empty: boolean }) {
  return (
    <div className="py-20 text-center px-6">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 border border-primary/30 mb-4">
        <Building2 className="h-5 w-5 text-primary" />
      </div>
      <h3 className="text-base font-semibold tracking-tight mb-1">
        {empty ? "Ningún cliente todavía" : "Ningún resultado"}
      </h3>
      <p className="text-sm text-muted-foreground mb-6">
        {empty
          ? "Agregá tu primer cliente para empezar a gestionar búsquedas."
          : "Probá con otro término de búsqueda o mostrá los inactivos."}
      </p>
      {empty && (
        <Button variant="primary" asChild>
          <Link to="/clients/new">
            <Plus className="h-4 w-4" />
            Nuevo cliente
          </Link>
        </Button>
      )}
    </div>
  );
}

interface RowProps {
  company: Company;
  onToggleActive: (is_active: boolean) => void;
}

function CompanyRow({ company, onToggleActive }: RowProps) {
  return (
    <li className="group hover:bg-accent/40 transition-colors">
      <Link
        to="/clients/$clientId"
        params={{ clientId: company.id }}
        className="flex items-center gap-4 px-4 py-3.5"
      >
        <div className="h-9 w-9 rounded-md bg-secondary border border-border flex items-center justify-center text-[11px] font-bold shrink-0">
          {company.name
            .split(/\s+/)
            .slice(0, 2)
            .map((p) => p[0]?.toUpperCase() ?? "")
            .join("")}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{company.name}</span>
            {!company.is_active && (
              <Badge variant="outline" className="text-[9px]">
                Inactiva
              </Badge>
            )}
            {company.rubro && (
              <Badge variant="muted" className="text-[9px] normal-case tracking-normal">
                {company.rubro}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
            {company.razon_social && (
              <span className="truncate">{company.razon_social}</span>
            )}
            {company.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {company.email}
              </span>
            )}
            {company.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {company.phone}
              </span>
            )}
          </div>
        </div>

        <div
          className="flex items-center gap-2 shrink-0"
          onClick={(e) => e.preventDefault()}
        >
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleActive(!company.is_active);
            }}
            className={cn(
              "text-[11px] px-2.5 py-1 rounded-md border transition-colors",
              company.is_active
                ? "border-success/30 bg-success/10 text-success hover:bg-success/15"
                : "border-border text-muted-foreground hover:border-success/30 hover:text-success"
            )}
          >
            {company.is_active ? "Activa" : "Activar"}
          </button>
          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-50 group-hover:opacity-100 transition-opacity" />
        </div>
      </Link>
    </li>
  );
}
